import type {
  MediaAgentLastAutomationDto,
  MediaAgentPoolMemberDto,
} from '@/types/dtos/media-agent/media-agent-pool.dto';

/** Same shape as the function returned by `useTranslations()`. */
export type MediaAgentTranslate = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

export type MediaAgentAutomationSeverity = 'info' | 'success' | 'warning' | 'error';

export type MediaAgentAutomationCaption = {
  text: string;
  severity: MediaAgentAutomationSeverity;
  inFlight: boolean;
  detail?: string;
};

type AutomationKindCopy = {
  labelKey: string;
  labelFallback: string;
  doneKey: string;
  doneFallback: string;
};

const KIND_PREFIX = 'media_agent_';

const JWT_REISSUE: AutomationKindCopy = {
  labelKey: 'admin.mediaAgentAdmin.automation.kind.jwtReissue',
  labelFallback: 'JWT reissue',
  doneKey: 'admin.mediaAgentAdmin.automation.kind.jwtReissueDone',
  doneFallback: 'Auto-reissued JWT',
};

const SOFT_RECOVER: AutomationKindCopy = {
  labelKey: 'admin.mediaAgentAdmin.automation.kind.softRecover',
  labelFallback: 'Soft recovery',
  doneKey: 'admin.mediaAgentAdmin.automation.kind.softRecoverDone',
  doneFallback: 'Soft recovery completed',
};

const HARD_RECOVER: AutomationKindCopy = {
  labelKey: 'admin.mediaAgentAdmin.automation.kind.hardRecover',
  labelFallback: 'Hard recovery',
  doneKey: 'admin.mediaAgentAdmin.automation.kind.hardRecoverDone',
  doneFallback: 'Hard recovery completed',
};

const AUTO_RECOVER: AutomationKindCopy = {
  labelKey: 'admin.mediaAgentAdmin.automation.kind.autoRecover',
  labelFallback: 'Recovery',
  doneKey: 'admin.mediaAgentAdmin.automation.kind.autoRecoverDone',
  doneFallback: 'Recovery completed',
};

const LIVE_HANDOFF: AutomationKindCopy = {
  labelKey: 'admin.mediaAgentAdmin.automation.kind.liveHandoff',
  labelFallback: 'Live handoff to spare shell',
  doneKey: 'admin.mediaAgentAdmin.automation.kind.liveHandoffDone',
  doneFallback: 'Handed live show to a spare shell',
};

const POOL_RETURN: AutomationKindCopy = {
  labelKey: 'admin.mediaAgentAdmin.automation.kind.poolReturn',
  labelFallback: 'Return to free pool',
  doneKey: 'admin.mediaAgentAdmin.automation.kind.poolReturnDone',
  doneFallback: 'Returned to the free pool',
};

/** Backend kind vocabulary is still settling, so each copy block accepts the plausible aliases. */
const KIND_ALIASES: ReadonlyArray<{ match: readonly string[]; copy: AutomationKindCopy }> = [
  {
    match: ['jwt_reissue', 'jwt_auto_reissued', 'auto_reissue', 'reissue', 'reissue_token', 'jwt_reinject'],
    copy: JWT_REISSUE,
  },
  { match: ['recover_soft', 'soft_recover', 'soft'], copy: SOFT_RECOVER },
  { match: ['recover_hard', 'hard_recover', 'hard'], copy: HARD_RECOVER },
  { match: ['live_handoff', 'handoff', 'failover'], copy: LIVE_HANDOFF },
  { match: ['returned_to_pool', 'return_to_pool', 'pool_return'], copy: POOL_RETURN },
  { match: ['recover_auto', 'auto_recover', 'recover'], copy: AUTO_RECOVER },
];

function interpolate(template: string, params?: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(params?.[name] ?? ''));
}

function translateWith(
  translate: MediaAgentTranslate,
  key: string,
  fallback: string,
  params?: Record<string, string | number>,
): string {
  return translate(key, params, interpolate(fallback, params));
}

function normalizeKind(kind: string): string {
  const trimmed = kind.trim().toLowerCase();
  return trimmed.startsWith(KIND_PREFIX) ? trimmed.slice(KIND_PREFIX.length) : trimmed;
}

/** `hard_recover_v2` → `Hard recover v2`, so an unmapped backend kind still reads as a sentence. */
function humanizeKind(kind: string): string {
  const words = normalizeKind(kind).replace(/[_-]+/g, ' ').trim();
  if (!words) {
    return '';
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function resolveKindLabel(translate: MediaAgentTranslate, kind: string, done: boolean): string {
  const normalized = normalizeKind(kind);
  const entry = KIND_ALIASES.find(({ match }) => match.some((alias) => normalized === alias));
  if (!entry) {
    return humanizeKind(kind);
  }
  return done
    ? translateWith(translate, entry.copy.doneKey, entry.copy.doneFallback)
    : translateWith(translate, entry.copy.labelKey, entry.copy.labelFallback);
}

/** Coarse relative age; ops only needs "is this fresh or from an hour ago". */
export function formatMediaAgentAutomationAge(
  translate: MediaAgentTranslate,
  at: string | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  if (!at) {
    return undefined;
  }
  const atMs = Date.parse(at);
  if (Number.isNaN(atMs)) {
    return undefined;
  }
  const elapsedSeconds = Math.max(0, Math.round((nowMs - atMs) / 1000));
  if (elapsedSeconds < 60) {
    return translateWith(translate, 'admin.mediaAgentAdmin.automation.age.justNow', 'just now');
  }
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) {
    return translateWith(translate, 'admin.mediaAgentAdmin.automation.age.minutes', '{{count}}m ago', {
      count: minutes,
    });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return translateWith(translate, 'admin.mediaAgentAdmin.automation.age.hours', '{{count}}h ago', { count: hours });
  }
  return translateWith(translate, 'admin.mediaAgentAdmin.automation.age.days', '{{count}}d ago', {
    count: Math.floor(hours / 24),
  });
}

/**
 * Row caption for the background heal that last touched this shell — e.g. `Auto-reissued JWT 2m ago`,
 * `Soft recovery in progress`, `Hard recovery failed — needs ops`. Returns `null` when the backend
 * has not sent `lastAutomation` yet.
 */
export function resolveMediaAgentAutomationCaption(
  translate: MediaAgentTranslate,
  lastAutomation: MediaAgentLastAutomationDto | undefined,
  nowMs: number = Date.now(),
): MediaAgentAutomationCaption | null {
  if (!lastAutomation?.kind) {
    return null;
  }

  const when = formatMediaAgentAutomationAge(translate, lastAutomation.at, nowMs) ?? '';
  const detail = lastAutomation.detail?.trim() || undefined;

  const build = (
    key: string,
    fallback: string,
    action: string,
    severity: MediaAgentAutomationSeverity,
    inFlight = false,
  ): MediaAgentAutomationCaption => ({
    text: translateWith(translate, key, fallback, { action, when }).replace(/\s+/g, ' ').trim(),
    severity,
    inFlight,
    detail,
  });

  switch (lastAutomation.result) {
    case 'in_flight':
      return build(
        'admin.mediaAgentAdmin.automation.inProgress',
        '{{action}} in progress',
        resolveKindLabel(translate, lastAutomation.kind, false),
        'info',
        true,
      );
    case 'failed':
      return build(
        'admin.mediaAgentAdmin.automation.failed',
        '{{action}} failed — needs ops',
        resolveKindLabel(translate, lastAutomation.kind, false),
        'error',
      );
    case 'skipped':
      return build(
        'admin.mediaAgentAdmin.automation.skipped',
        '{{action}} skipped {{when}}',
        resolveKindLabel(translate, lastAutomation.kind, false),
        'warning',
      );
    case 'succeeded':
      return build(
        'admin.mediaAgentAdmin.automation.succeeded',
        '{{action}} {{when}}',
        resolveKindLabel(translate, lastAutomation.kind, true),
        'success',
      );
    default:
      return build(
        'admin.mediaAgentAdmin.automation.unknown',
        '{{action}} · {{when}}',
        resolveKindLabel(translate, lastAutomation.kind, false),
        'info',
      );
  }
}

export type MediaAgentStartRetryCaption = {
  text: string;
  severity: 'info' | 'error';
  detail?: string;
};

/**
 * Caption for a paused row, covering Azure VM start retries after a transient capacity failure.
 * Exhausted outranks pending so a VM that needs ops is never masked by a sibling still retrying,
 * and the text, colour and tooltip all read from this one decision.
 */
export function resolveMediaAgentStartRetryCaption(
  translate: MediaAgentTranslate,
  vms: MediaAgentPoolMemberDto['vms'],
): MediaAgentStartRetryCaption {
  const exhausted = vms.find((vm) => vm.startRetry?.status === 'exhausted');
  if (exhausted) {
    return {
      text: translateWith(translate, 'admin.mediaAgentAdmin.startRetryExhausted', 'Start unavailable — needs ops'),
      severity: 'error',
      detail: exhausted.startRetry?.lastError,
    };
  }

  const pending = vms.find((vm) => vm.startRetry?.status === 'pending');
  if (pending) {
    return {
      text: translateWith(translate, 'admin.mediaAgentAdmin.startRetrying', 'Start retrying'),
      severity: 'info',
      detail: pending.startRetry?.lastError,
    };
  }

  return { text: translateWith(translate, 'admin.mediaAgentAdmin.paused', 'Paused'), severity: 'info' };
}

/** Shell is up but its player JWT is rejected, so it cannot serve a show until reissued or re-paired. */
export function hasMediaAgentAuthIssue(agent: Pick<MediaAgentPoolMemberDto, 'authHealth'>): boolean {
  return agent.authHealth === 'jwt_stale';
}

export function isMediaAgentAutomationInFlight(agent: Pick<MediaAgentPoolMemberDto, 'lastAutomation'>): boolean {
  return agent.lastAutomation?.result === 'in_flight';
}

/** Auto-heal is out of ideas (or never ran) — the manual Recover escape hatch gets emphasized. */
export function isMediaAgentHealStuck(agent: Pick<MediaAgentPoolMemberDto, 'authHealth' | 'lastAutomation'>): boolean {
  if (isMediaAgentAutomationInFlight(agent)) {
    return false;
  }
  return hasMediaAgentAuthIssue(agent) || agent.lastAutomation?.result === 'failed';
}
